import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { useCreateNote, useSpace, useSpaceTree } from "@/api/hooks";
import { errorMessage } from "@/api/client";
import type { NoteTreeNode } from "@/api/types";
import { isMac } from "@/lib/utils";
import { useToast } from "@/lib/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MarkdownEditor } from "@/components/MarkdownEditor";
import { OutlineRail, columnClass, frameClass, hasRail } from "@/components/OutlineRail";
import type { OutlineItem } from "@/lib/blocks";
import { TreePicker } from "@/components/TreePicker";

/**
 * New note as a page: `/spaces/:id/new`, with `?parent=<noteId>` when started from a
 * note ("New child page"). Mirrors the edit page's layout so writing feels the same.
 */
export default function NoteNewPage() {
  const { id: spaceId = "" } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const initialParent = params.get("parent");
  const navigate = useNavigate();
  const { toast } = useToast();
  const space = useSpace(spaceId);
  const tree = useSpaceTree(spaceId);
  const create = useCreateNote(spaceId);

  const [title, setTitle] = useState("");
  const [markdown, setMarkdown] = useState("");
  const [parentId, setParentId] = useState<string | null>(initialParent);
  const [pickParent, setPickParent] = useState(false);
  const [outline, setOutline] = useState<OutlineItem[]>([]);
  const onParsed = useCallback((p: { outline: OutlineItem[] }) => setOutline(p.outline), []);
  const rail = hasRail(outline);
  const dirty = title.trim() !== "" || markdown.trim() !== "";
  const canSave = dirty && !create.isPending;

  const parentTitle = useMemo(() => (parentId ? findTitle(tree.data, parentId) : undefined), [tree.data, parentId]);
  const backTo = initialParent ? `/notes/${initialParent}` : `/spaces/${spaceId}`;

  useEffect(() => {
    if (!dirty) return;
    const onUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [dirty]);

  function save() {
    const t = title.trim();
    const body = markdown.trim() ? markdown : t ? `# ${t}\n` : "";
    if (!t && !body) return;
    create.mutate(
      { title: t || undefined, markdown: body, parentId },
      {
        onSuccess: (note) => navigate(`/notes/${note.id}`, { replace: true }),
        onError: (err) => toast({ title: "Could not create note", description: errorMessage(err), tone: "error" }),
      },
    );
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (canSave) save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // re-bound each render so it sees the latest fields

  return (
    <div className={frameClass(rail)}>
      <div className={columnClass(rail)}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <Link to={backTo} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> {initialParent ? parentTitle ?? "Back" : space.data?.name ?? "Space"}
          </Link>
          <h1 className="mt-1 text-xl font-semibold tracking-tight">New note</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to={backTo}>Cancel</Link>
          </Button>
          <Button size="sm" onClick={save} disabled={!canSave}>
            <Save /> {create.isPending ? "Creating…" : "Create note"}
            <kbd className="ml-1 hidden rounded border border-primary-foreground/30 px-1 font-mono text-[10px] opacity-80 sm:inline">{isMac() ? "⌘" : "Ctrl"}S</kbd>
          </Button>
        </div>
      </div>

      <form
        className="grid gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSave) save();
        }}
      >
        <div className="grid gap-1.5">
          <Label htmlFor="nn-title">Title</Label>
          <Input id="nn-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Weekly review" autoFocus />
          <p className="text-xs text-muted-foreground">Optional when the Markdown starts with a heading; the first heading becomes the title.</p>
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="nn-md">Markdown</Label>
          <MarkdownEditor id="nn-md" value={markdown} onChange={setMarkdown} onParsed={onParsed} placeholder={"Plain prose, or a component. Use Insert for a template."} />
        </div>

        <div className="grid gap-1.5">
          <div className="flex items-center justify-between">
            <Label>Parent page</Label>
            <button type="button" className="text-xs text-link underline-offset-2 hover:underline" onClick={() => setPickParent((p) => !p)}>
              {pickParent ? "Done" : "Change"}
            </button>
          </div>
          {pickParent ? (
            <TreePicker tree={tree.data} loading={tree.isLoading} value={parentId} onChange={setParentId} />
          ) : (
            <div className="rounded-lg border bg-muted/30 px-3 py-2 text-sm">{parentId === null ? "Top level" : parentTitle ?? (tree.isLoading ? "…" : "Selected page")}</div>
          )}
        </div>
      </form>
      </div>
      {rail && <OutlineRail outline={outline} links={false} />}
    </div>
  );
}

function findTitle(tree: NoteTreeNode[] | undefined, id: string): string | undefined {
  if (!tree) return undefined;
  for (const n of tree) {
    if (n.id === id) return n.title || "Untitled";
    const t = findTitle(n.children, id);
    if (t) return t;
  }
  return undefined;
}
